import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import FormHelperText from "@mui/material/FormHelperText";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Controller, useForm } from "react-hook-form";
import { useTheme } from "@mui/material/styles";
import { frenchNameRegex, emailRegex } from "@shared/utils/commonUtils";
import useEnvironment from "@shared/hooks/useEnvironment";
import ReCAPTCHA from "react-google-recaptcha";
import Icon from "@shared/components/Icon";
import ContactService from "@project/services/ContactService";
import CaptchaService from "@project/services/CaptchaService";
import useCookieStore from "@shared/store/CookieStore";
import ContactTextField from "./ContactTextField";
import ContactFormConsent from "./ContactFormConsent";
import Stack from "@mui/material/Stack";
// accepte les formats français courants : 0612345678, 06 12 34 56 78, +33 6 12 34 56 78
const phoneRegex = /^(?:\+33|0)\s?[1-9](?:[\s.-]?\d{2}){4}$/;
const interestOptions = [
    {
        label: "Pack Cyber",
        description: "Protéger l’essentiel de votre environnement numérique",
    },
    {
        label: "Modules outils",
        description: "Renforcer votre protection cyber selon vos priorités",
    },
    {
        label: "Prestations expertes",
        description: "Structurer votre trajectoire avec un expert",
    },
    {
        label: "Je ne sais pas encore",
        description: "",
    },
];
const loaderStyled = {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    bgcolor: "#eff4fd",
    opacity: "0.5",
    zIndex: 1000,
    "& svg": {
        color: "secondary.main",
    },
};
const formValidatedStyled = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    px: 0,
    py: 10,
};
const formContainerStyled = {
    p: {
        xs: "24px 24px 40px 24px !important",
        md: "48px 56px !important",
    },
    backgroundColor: "background.grey",
    width: "100%",
};
export const recaptchaStyled = {
    "& .grecaptcha-badge": {
        bottom: { xs: "123px !important", lg: 0 },
        zIndex: "525",
    },
};
const defaultFormValues = {
    civility: "",
    name: "",
    firstName: "",
    email: "",
    phone: "",
    organisation: "",
    interests: [],
    commentary: "",
    consent: false,
    website: "", // honeypot anti-spam, doit rester vide
};
// masqué visuellement mais reste dans le DOM/tabulation pour piéger les bots
const honeypotStyled = {
    position: "absolute",
    width: 0,
    height: 0,
    padding: 0,
    margin: 0,
    border: 0,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
};
const ContactForm = (props) => {
    const { id, recipient, ...others } = props;
    const theme = useTheme();
    const { recaptcha_site_key_invisible, mail_attachment_size = 10 * 1024 * 1024, // 10MB
    mail_allowed_attachment_type, } = useEnvironment();
    const [loader, setLoader] = useState(false);
    const [formSended, setFormSended] = useState(false);
    const [sentFirstName, setSentFirstName] = useState("");
    const [sendError, setSendError] = useState(null);
    const [filesForm, setFilesForm] = useState(null);
    const recaptchaRef = useRef(null);
    const pendingFormValuesRef = useRef(null);
    const { control, handleSubmit, reset, register, formState: { errors, isSubmitted, isValid }, } = useForm({
        mode: "onChange",
        defaultValues: defaultFormValues,
    });
    const { googleRecaptchaConsent } = useCookieStore((state) => ({
        googleRecaptchaConsent: state.googleRecaptchaConsent,
    }));
    const filesSize = (Array.isArray(filesForm) &&
        filesForm.reduce((totalSize, fileForm) => totalSize + (fileForm?.size || 0), 0)) ||
        0;
    const fileFormError = Boolean(filesSize > mail_attachment_size);
    const showError = (key) => {
        if (!isSubmitted) {
            return false;
        }
        if (key === "fileForm") {
            return fileFormError;
        }
        return Boolean(errors[key]);
    };
    const isFormValid = isValid && !fileFormError;
    const sendMail = async (values) => {
        const formData = new FormData();
        Array.isArray(filesForm) &&
            filesForm.forEach((fileForm) => {
                formData.append("attachment", fileForm, fileForm.name);
            });
        formData.append("recipient", recipient || "");
        formData.append("civility", values.civility);
        formData.append("name", values.name);
        formData.append("firstName", values.firstName);
        formData.append("email", values.email);
        formData.append("phone", values.phone);
        formData.append("organisation", values.organisation);
        formData.append("interests", values.interests.join(", "));
        formData.append("commentary", decodeURIComponent(encodeURIComponent(values.commentary).replace(/%0A/g, "<br/>")));
        try {
            const response = await ContactService.postMail(formData);
            if (response &&
                response.status &&
                ![200, 201, 202].includes(response.status)) {
                setSendError(response.message);
                return;
            }
            setFormSended(true);
            setSentFirstName(values.firstName || "");
            reset(defaultFormValues);
            setFilesForm(null);
        }
        finally {
            setLoader(false);
        }
    };
    const checkUser = async (value) => {
        const recaptchaValidated = await CaptchaService.verifyCaptcha(value);
        if (recaptchaValidated.success && pendingFormValuesRef.current) {
            await sendMail(pendingFormValuesRef.current);
            pendingFormValuesRef.current = null;
        }
        else {
            setLoader(false);
        }
    };
    const handleValidate = (values) => {
        if (values.website) {
            // champ honeypot rempli : soumission de bot on ignore
            setFormSended(true);
            setSentFirstName(values.firstName || "");
            reset(defaultFormValues);
            return;
        }
        if (!isFormValid) {
            return;
        }
        setLoader(true);
        setSendError(null);
        if (!recaptcha_site_key_invisible) {
            sendMail(values);
        }
        else {
            pendingFormValuesRef.current = values;
            recaptchaRef.current?.execute();
        }
    };
    const handleUploadFile = (e) => {
        const fileToUpload = e.target.files[0];
        if (!fileToUpload) {
            return;
        }
        setFilesForm(filesForm ? [...filesForm, fileToUpload] : [fileToUpload]);
        e.target.value = null;
    };
    const handleRemoveFile = (fileFormName) => {
        setFilesForm(filesForm && filesForm.length > 1
            ? filesForm.filter((fileForm) => fileForm.name !== fileFormName)
            : null);
    };
    const handleBackToForm = () => {
        setFormSended(false);
        setSentFirstName("");
        setSendError(null);
        reset(defaultFormValues);
    };
    if (formSended) {
        return (_jsx(Box, { sx: {
                ...formContainerStyled,
                height: { xs: "auto", lg: "stretch" },
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }, ...others, children: _jsxs(Stack, { spacing: 3, sx: formValidatedStyled, role: "status", "aria-live": "polite", children: [_jsx(Box, { sx: {
                            backgroundColor: theme.palette.success.light,
                            p: 1,
                            borderRadius: "50%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }, children: _jsx(Icon, { icon: "check-circle", type: "far", color: theme.palette.success.main, style: { fontSize: 28 } }) }), _jsxs(Stack, { spacing: 1, children: [_jsx(Typography, { variant: "h5", component: "p", sx: {
                                    fontSize: "16px",
                                    fontWeight: "600",
                                }, children: sentFirstName
                                    ? `Merci ${sentFirstName}, votre message a bien été envoyé`
                                    : "Votre message a bien été envoyé" }), _jsx(Typography, { variant: "body2", component: "p", children: "Un expert Docaposte vous contactera dans les plus brefs d\u00E9lais." })] }), _jsx(Button, { variant: "outlined", onClick: handleBackToForm, sx: { mt: 2 }, "aria-label": "Revenir au formulaire de contact", children: "Retour au formulaire" })] }) }, id));
    }
    return (_jsxs(Box, { sx: formContainerStyled, ...others, children: [_jsx(Typography, { variant: "caption", component: "p", sx: { mb: 1, height: "14px" }, children: "* Les champs marqu\u00E9s d\u2019un ast\u00E9risque sont obligatoires." }), _jsxs(Stack, { spacing: 2, component: "form", action: "/", method: "POST", onSubmit: handleSubmit(handleValidate), autoComplete: "off", noValidate: true, role: "form", "aria-label": "Formulaire de contact", children: [loader && (_jsx(Grid, { container: true, justifyContent: "center", alignItems: "center", sx: loaderStyled, children: _jsx(CircularProgress, {}) })), _jsxs(Stack, { direction: { xs: "column", lg: "row" }, spacing: 3, sx: {
                            alignItems: { xs: "stretch", md: "stretch", lg: "flex-start" },
                        }, children: [_jsx(Box, { sx: { flexShrink: 0, width: { xs: "100%", md: "100%", lg: 200 } }, children: _jsx(ContactTextField, { name: "civility", label: "Civilit\u00E9 *", control: control, rules: {
                                        required: "Votre civilité est requise",
                                    }, showError: showError, errors: errors, onResetError: () => setSendError(null), selectOptions: [
                                        { value: "", label: "" },
                                        { value: "Mme. ", label: "Madame" },
                                        { value: "M. ", label: "Monsieur" },
                                        { value: "Autre ", label: "Autre" },
                                    ] }) }), _jsx(Box, { sx: { flex: 1, minWidth: 0 }, children: _jsx(ContactTextField, { name: "name", label: "Nom *", control: control, rules: {
                                        required: "Votre nom est requis",
                                        pattern: {
                                            value: frenchNameRegex,
                                            message: "Votre nom n'est pas valide",
                                        },
                                    }, showError: showError, errors: errors, onResetError: () => setSendError(null) }) }), _jsx(Box, { sx: { flex: 1, minWidth: 0 }, children: _jsx(ContactTextField, { name: "firstName", label: "Pr\u00E9nom *", control: control, rules: {
                                        required: "Votre prénom est requis",
                                        pattern: {
                                            value: frenchNameRegex,
                                            message: "Votre prénom n'est pas valide",
                                        },
                                    }, showError: showError, errors: errors, onResetError: () => setSendError(null) }) })] }), _jsxs(Stack, { direction: { xs: "column", lg: "row" }, spacing: 3, sx: {
                            alignItems: { xs: "stretch", md: "stretch", lg: "flex-start" },
                        }, children: [_jsx(ContactTextField, { name: "email", label: "Email professionnel *", control: control, rules: {
                                    required: "Votre adresse e-mail est requise",
                                    pattern: {
                                        value: emailRegex,
                                        message: "Le format de l'adresse e-mail n'est pas correct",
                                    },
                                }, showError: showError, errors: errors, onResetError: () => setSendError(null), exampleText: "Exemple : m-dupond@gmail.com" }), _jsx(ContactTextField, { name: "phone", label: "T\u00E9l\u00E9phone *", control: control, rules: {
                                    required: "Votre numéro de téléphone est requis",
                                    validate: (value) => !value ||
                                        phoneRegex.test(value) ||
                                        "Le format du numéro de téléphone n'est pas valide",
                                }, showError: showError, errors: errors, onResetError: () => setSendError(null), type: "tel", exampleText: "Exemple : 0654238935" })] }), _jsx(ContactTextField, { name: "organisation", label: "Organisation *", control: control, rules: {
                            required: "Votre organisation est requise",
                        }, showError: showError, errors: errors, onResetError: () => setSendError(null) }), _jsxs(Box, { component: "fieldset", sx: { border: 0, p: 0, m: 0 }, children: [_jsx(Typography, { component: "legend", variant: "body2", sx: { mb: 1 }, children: "Vous \u00EAtes int\u00E9ress\u00E9 par :" }), _jsx(Controller, { name: "interests", control: control, render: ({ field }) => (_jsx(Box, { sx: {
                                        display: "grid",
                                        gridTemplateColumns: {
                                            xs: "1fr",
                                            sm: "repeat(2, 1fr)",
                                            lg: "repeat(4, 1fr)",
                                        },
                                        gap: 1,
                                    }, children: interestOptions.map(({ label, description }) => (_jsx(Tooltip, { title: description, placement: "top", enterDelay: 250, enterNextDelay: 250, slotProps: { tooltip: { sx: { lineHeight: 1.5 } } }, children: _jsx(FormControlLabel, { label: _jsx(_Fragment, { children: _jsx(Typography, { component: "span", sx: {
                                                        display: "block",
                                                        fontSize: "14px !important",
                                                        fontWeight: 500,
                                                        lineHeight: 1.4,
                                                    }, children: label }) }), sx: { m: 0, alignItems: "flex-start" }, control: _jsx(Checkbox, { size: "small", sx: { p: 0, mr: 1 }, checked: field.value.includes(label), onChange: (event) => field.onChange(event.target.checked
                                                    ? [...field.value, label]
                                                    : field.value.filter((value) => value !== label)) }) }) }, label))) })) })] }), _jsx(ContactTextField, { name: "commentary", label: "Message", control: control, showError: showError, errors: errors, onResetError: () => setSendError(null), multiline: true, rows: 3, exampleText: "Exemple : J\u2019aimerais obtenir plus d\u2019informations sur..." }), _jsx(ContactFormConsent, { control: control, showError: showError, onResetError: () => setSendError(null) }), recaptcha_site_key_invisible && googleRecaptchaConsent && (_jsx(Box, { sx: recaptchaStyled, role: "img", "aria-label": "V\u00E9rification reCAPTCHA en cours", children: _jsx(ReCAPTCHA, { ref: recaptchaRef, size: "invisible", sitekey: recaptcha_site_key_invisible, onChange: (recaptchaValue) => {
                                checkUser(recaptchaValue);
                            }, "aria-label": "V\u00E9rification reCAPTCHA invisible" }) })), _jsx(Box, { display: "flex", flexDirection: "row-reverse", children: _jsx(Tooltip, { title: recaptcha_site_key_invisible && !googleRecaptchaConsent
                                ? 'Vous devez accepter les cookies "Google reCAPTCHA" pour utiliser cette fonctionnalité'
                                : "", children: _jsx(Box, { children: _jsx(Button, { disabled: !!recaptcha_site_key_invisible && !googleRecaptchaConsent, variant: "contained", color: "secondary", type: "submit", "aria-label": `${!isFormValid ? "Formulaire incomplet. " : ""}Envoyer le formulaire de contact`, sx: { padding: { xs: "12px 16px", md: "16px 20px" } }, children: "Envoyer la demande" }) }) }) }), sendError && (_jsxs(FormHelperText, { error: true, sx: { fontSize: "1rem" }, role: "alert", children: [_jsx(Icon, { icon: "exclamation-circle", title: "Erreur" }), "\u00A0", sendError] })), _jsx(Box, { component: "input", type: "text", tabIndex: -1, autoComplete: "off", "aria-hidden": "true", sx: honeypotStyled, ...register("website") })] })] }, id));
};
export default ContactForm;
